// src/components/agenda/PollForm.js
import React, { useState, useEffect } from 'react';
import { generateId } from '../../services/utils';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase'; // Passe diesen Pfad ggf. an

// *** GEÄNDERT: 'existingPoll' als Prop hinzugefügt ***
const PollForm = ({
  onSave, // onSave ist jetzt 'addDoc' ODER 'updateDoc'
  onCancel,
  setNotification,
  companyId,
  meetingId,
  agendaItemId,
  currentUserId,
  existingPoll // Wenn diese Prop existiert, sind wir im Bearbeiten-Modus
}) => {
  
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState([{ id: generateId(), text: '' }, { id: generateId(), text: '' }]);
  const [isLoading, setIsLoading] = useState(false);
  
  // *** NEU: Prüfen, ob wir im Bearbeiten-Modus sind ***
  const isEditing = !!existingPoll;

  // *** NEU: Formular mit bestehenden Daten füllen ***
  useEffect(() => {
    if (isEditing) {
      setQuestion(existingPoll.question);
      // Stelle sicher, dass Optionen IDs haben (alte Daten haben vielleicht keine)
      setOptions(existingPoll.options.map(opt => ({ id: opt.id || generateId(), text: opt.text })));
    }
  }, [isEditing, existingPoll]);


  const handleOptionChange = (id, text) => {
    setOptions(options.map(opt => opt.id === id ? { ...opt, text } : opt));
  };

  const handleAddOption = () => {
    setOptions([...options, { id: generateId(), text: '' }]);
  };

  const handleRemoveOption = (id) => {
    setOptions(options.filter(opt => opt.id !== id));
  };

  // *** GEÄNDERT: Unterscheidet zwischen Erstellen und Bearbeiten ***
  const handleSaveClick = async () => {
    const validOptions = options.filter(opt => opt.text.trim() !== '');
    if (question.trim() === '' || validOptions.length < 2) {
      setNotification({ message: 'Bitte geben Sie eine Frage und mindestens zwei Optionen ein.', type: 'error' });
      return;
    }
    
    setIsLoading(true);

    // Datenobjekt für beide Fälle
    const pollData = {
      question: question.trim(),
      // Entferne 'votes', falls es beim Bearbeiten vorhanden war
      options: validOptions.map(opt => ({ id: opt.id, text: opt.text })), 
    };

    if (isEditing) {
        // --- FALL 1: BEARBEITEN (ruft updateDoc in PollDisplay auf) ---
        // Die 'onSave'-Funktion wurde von PollDisplay als 'handleUpdatePoll' übergeben
        try {
            // 'onSave' ist hier handleUpdatePoll aus PollDisplay
            await onSave(pollData); 
            // setNotification und onCancel werden von PollDisplay gehandhabt (nachdem updateDoc erfolgreich war)
        } catch (error) {
             setNotification({ message: `Update fehlgeschlagen: ${error.message}`, type: 'error' });
        }
    } else {
        // --- FALL 2: NEU ERSTELLEN (wie zuvor) ---
        if (!companyId || !meetingId || !agendaItemId || !currentUserId) {
            setNotification({ message: 'Fehler: Kontext (Firma, Meeting) fehlt.', type: 'error' });
            setIsLoading(false);
            return;
        }
        
        const newPollData = {
          ...pollData,
          status: 'pending', // Startet als 'pending'
          results: {},
          agendaItemId: agendaItemId,
          createdBy: currentUserId,
          createdAt: serverTimestamp()
          // 'id' wird von Firestore automatisch vergeben
        };
        
        try {
            const pollsColRef = collection(db, `companies/${companyId}/meetings/${meetingId}/polls`);
            await addDoc(pollsColRef, newPollData);
            setNotification({ message: 'Umfrage erfolgreich erstellt.', type: 'success' });
            onCancel(); // Schließt das Formular
        } catch (error) {
            console.error("Error creating poll: ", error);
            setNotification({ message: `Fehler beim Erstellen der Umfrage: ${error.message}`, type: 'error' });
        }
    }
    setIsLoading(false);
  };

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-gray-800 dark:to-gray-900 p-4 rounded-2xl shadow-lg mb-5 border border-yellow-200 dark:border-yellow-700">
      <h4 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
        {isEditing ? 'Umfrage bearbeiten' : 'Neue Umfrage erstellen'}
      </h4>
      <input
        type="text"
        placeholder="Umfragefrage"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white transition-colors duration-200 mb-3"
        required
        disabled={isLoading}
      />
      <div className="space-y-2 mb-3">
        {options.map((opt, index) => (
          <div key={opt.id} className="flex items-center gap-2">
            <input
              type="text"
              placeholder={`Option ${index + 1}`}
              value={opt.text}
              onChange={(e) => handleOptionChange(opt.id, e.target.value)}
              className="flex-grow p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white transition-colors duration-200"
              required
              disabled={isLoading}
            />
            {options.length > 2 && (
              <button
                type="button"
                onClick={() => handleRemoveOption(opt.id)}
                className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200"
                title="Option entfernen"
                disabled={isLoading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={handleAddOption}
        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 ease-in-out transform hover:scale-105 text-sm font-medium shadow-sm mb-4"
        disabled={isLoading}
      >
        Option hinzufügen
      </button>
      <div className="flex justify-end space-x-3 mt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-all duration-200 ease-in-out transform hover:scale-105 font-medium shadow-sm"
          disabled={isLoading}
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleSaveClick}
          className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 ease-in-out transform hover:scale-105 font-medium shadow-md ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={isLoading}
        >
          {isLoading ? 'Speichert...' : (isEditing ? 'Änderungen speichern' : 'Umfrage erstellen')}
        </button>
      </div>
    </div>
  );
};

export default PollForm;

